import { finder } from '@medv/finder';

export interface AnchoredSelector {
  cssSelector: string;
  xpath: string;
  elementText: string;
  pinX: number;
  pinY: number;
}

/**
 * Build a robust selector descriptor for a clicked element.
 *
 * - `cssSelector`: short, unique CSS selector via @medv/finder.
 * - `xpath`: full ancestor-walk XPath as a backup.
 * - `elementText`: first 100 chars of textContent for a last-ditch text match.
 * - `pinX/pinY`: click position normalized to [0,1] within the element's bbox,
 *   so the pin sits on roughly the same spot even if the element resizes.
 */
export function getSelector(el: Element, clickEvent?: { clientX: number; clientY: number }): AnchoredSelector {
  const cssSelector = (() => {
    try {
      return finder(el as HTMLElement, {
        seedMinLength: 1,
        optimizedMinLength: 2,
        threshold: 1000,
      });
    } catch {
      return el.tagName.toLowerCase();
    }
  })();

  const xpath = computeXPath(el);
  const elementText = (el.textContent ?? '').trim().slice(0, 100);

  const rect = el.getBoundingClientRect();
  let pinX = 0.5;
  let pinY = 0.5;
  if (clickEvent && rect.width > 0 && rect.height > 0) {
    pinX = Math.min(1, Math.max(0, (clickEvent.clientX - rect.left) / rect.width));
    pinY = Math.min(1, Math.max(0, (clickEvent.clientY - rect.top) / rect.height));
  }

  return { cssSelector, xpath, elementText, pinX, pinY };
}

function computeXPath(el: Element): string {
  if (el.id) return `//*[@id="${cssEscape(el.id)}"]`;
  const segments: string[] = [];
  let current: Element | null = el;
  while (current && current.nodeType === Node.ELEMENT_NODE && current !== document.documentElement) {
    let index = 1;
    let sibling = current.previousElementSibling;
    while (sibling) {
      if (sibling.tagName === current.tagName) index++;
      sibling = sibling.previousElementSibling;
    }
    segments.unshift(`${current.tagName.toLowerCase()}[${index}]`);
    current = current.parentElement;
  }
  return `/html/${segments.join('/')}`;
}

function cssEscape(s: string): string {
  return s.replace(/"/g, '\\"');
}

/**
 * Resolve a stored selector back to an element on the current page.
 * Tries CSS → XPath → text-based heuristic in order.
 */
export function resolveSelector(sel: {
  cssSelector: string;
  xpath: string;
  elementText: string;
}): Element | null {
  // 1. CSS
  try {
    const found = document.querySelector(sel.cssSelector);
    if (found) return found;
  } catch {
    // selector may be invalid on this page
  }

  // 2. XPath
  try {
    const xr = document.evaluate(
      sel.xpath,
      document,
      null,
      XPathResult.FIRST_ORDERED_NODE_TYPE,
      null,
    );
    if (xr.singleNodeValue && xr.singleNodeValue.nodeType === Node.ELEMENT_NODE) {
      return xr.singleNodeValue as Element;
    }
  } catch {
    // bad xpath
  }

  // 3. Text heuristic
  if (sel.elementText && sel.elementText.length >= 4) {
    const all = document.body?.querySelectorAll<HTMLElement>('*');
    if (all) {
      for (const el of all) {
        const txt = (el.textContent ?? '').trim();
        if (txt && txt.length < 500 && txt.startsWith(sel.elementText)) {
          return el;
        }
      }
    }
  }

  return null;
}

/**
 * Compute the page-absolute pixel position of a pin given the resolved element
 * and the normalized [0,1] coordinates stored on the comment. Includes window
 * scroll so the caller can position an absolutely-positioned element relative
 * to the document, not the viewport.
 */
export function getPinPosition(el: Element, pinX: number, pinY: number): { left: number; top: number } {
  const rect = el.getBoundingClientRect();
  return {
    left: rect.left + window.scrollX + pinX * rect.width,
    top: rect.top + window.scrollY + pinY * rect.height,
  };
}
